import React, {
    useRef,
    useEffect
} from 'react';

import {
    useShellAlert,
    useUpdateShellPrimary,
    useUpdateShellSecondary
} from '../../shell/hooks/shellHooks.js';

import {
    ConfigurationProvider,
    useCurationConfigValue,
    useGetFocusedInstanceIdValue,
    useSetCurationConfigState
} from '../../../hooks/widgetConfiguration.js';

import {
    LoadingStatus,
    useSetInstanceLoadingStatus
} from '../../../hooks/loadingStatusHooks.js';

import {
    SavedSearchListWidget,
    SampleTopicsListWidget
} from '../components/SavedSearchListWidget.jsx';

import useGetCurationTitle from '../hooks/useGetCurationTitle.js';
import useListeningSearchTimePeriod from '../hooks/useListeningSearchTimePeriod.js';
import PoweredByQuid from '../components/PoweredByQuid.jsx';
import SavedTopicsUpsellWidget from '../components/SavedTopicsUpsellWidget.jsx';

import {
    CreateSavedSearchButton
} from '../components/CreateSavedSearchButton.jsx';

import {
    listeningDefinitions
} from '../components/enums/listeningDefinitions.jsx';

import useHasCompanionMode from '../hooks/useHasCompanionMode.js';
import useHasValidQuidMonitorToken from '../hooks/useHasValidQuidMonitorToken.js';
import InvalidQuidMonitorTokenErrorPage from '../components/InvalidQuidMonitorTokenErrorPage.jsx';

const pdfExportConfig = {
    exportOrientation: RivalIQ.enums.orientation.LANDSCAPE,
    exportWidth: 970
};

function CurationLayout() {
    const hasShownAlert = useRef(true);
    const {info: alertInfo} = useShellAlert();
    const setTimePeriod = useSetCurationConfigState(null, 'timePeriod');

    const plan = useCurationConfigValue('plan');

    const {
        description: timePeriodDescription,
        message: timePeriodMessage,
        override: timePeriodOverride,
        period: timePeriod,
        restrictions: dateRangeRestrictions
    } = useListeningSearchTimePeriod(!plan?.has_full_instant_search, false);

    useEffect(() => {
        if (hasShownAlert.current && timePeriodOverride) {
            hasShownAlert.current = false;
            setTimePeriod(timePeriodOverride);
            alertInfo(listeningDefinitions.shell.timePeriodChange(timePeriod));
        }
    }, [alertInfo, setTimePeriod, timePeriod, timePeriodOverride]);

    const curationTitle = useGetCurationTitle('Saved Searches');
    useUpdateShellPrimary(curationTitle, {
        companySets: {
            disabled: true
        },
        dataFreshness: {
            disabled: true
        },
        timePeriodDateRangeRestrictions: dateRangeRestrictions,
        timePeriod: timePeriod,
        timePeriodMessage: timePeriodMessage,
        timePeriodDescription: timePeriodDescription
    });

    useUpdateShellSecondary(false);

    const updateLoadingStatus = useSetInstanceLoadingStatus();

    const onlyInstanceId = useCurationConfigValue('onlyInstanceId');
    const showOnlyInstanceId = useGetFocusedInstanceIdValue() || onlyInstanceId;

    useEffect(() => {
        updateLoadingStatus(LoadingStatus.COMPLETE);
    });

    const hasCompanionMode = useHasCompanionMode();
    const hasValidQuidMonitorToken = useHasValidQuidMonitorToken();
    if (hasCompanionMode && !hasValidQuidMonitorToken) {
        return <InvalidQuidMonitorTokenErrorPage/>;
    }

    const widgets = {
        'create-saved-search-button': <CreateSavedSearchButton
            instanceId='create-saved-search-button'
        />,
        'topics-upsell': <SavedTopicsUpsellWidget
            instanceId='topics-upsell'/>,
        'saved-searches': <SavedSearchListWidget
            instanceId='saved-searches'
            pdfExportConfig={pdfExportConfig}
        />,
        'sample-topics': <SampleTopicsListWidget
            instanceId='sample-topics'
        />
    };

    function getWidget(instanceId) {
        if (!showOnlyInstanceId || instanceId === showOnlyInstanceId) {
            return widgets[instanceId];
        }
    }

    return (
        <>
            {getWidget('create-saved-search-button')}
            {getWidget('topics-upsell')}
            {getWidget('saved-searches')}
            {getWidget('sample-topics')}

            <PoweredByQuid />
        </>
    );
}

export default function ListeningCuration(props) {

    const defaultProps = {
        instanceId: props?.instanceId,
        scheduleId: props?.scheduleId,
        wProps: {
            'saved-searches': {
                sortMetricName: 'shareOfVoice',
                sortOrder: 'desc'
            },
            'sample-topics': {
                sortMetricName: 'shareOfVoice',
                sortOrder: 'desc'
            }
        }
    };

    const configurationSchema = {
        widgetPropsConfig: {
            'saved-searches': {
                sortMetricName: {
                    urlPersistence: true
                },
                sortOrder: {
                    urlPersistence: true
                }
            },
            'sample-topics': {
                sortMetricName: {
                    urlPersistence: true
                },
                sortOrder: {
                    urlPersistence: true
                }
            }
        }
    };

    return (
        <ConfigurationProvider
            defaultProps={defaultProps}
            configurationSchema={configurationSchema}
        >
            <CurationLayout />
        </ConfigurationProvider>
    );
}
